import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Shield, Plus, AlertTriangle, CheckCircle, Search, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  attiva: 'bg-green-100 text-green-800',
  scaduta: 'bg-red-100 text-red-800',
  mancante: 'bg-yellow-100 text-yellow-800'
};

export default function Coperture() {
  const [coperture, setCoperture] = useState([]);
  const [volontari, setVolontari] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVolontario, setSelectedVolontario] = useState('');
  const [formData, setFormData] = useState({
    stato: 'attiva',
    numero_polizza: '',
    compagnia: '',
    decorrenza: '',
    scadenza: '',
    tipologia: '',
    note: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [copertureRes, volontariRes] = await Promise.all([
        fetch(`${API_URL}/api/coperture`, { credentials: 'include' }),
        fetch(`${API_URL}/api/volontari`, { credentials: 'include' })
      ]);

      if (copertureRes.ok) {
        setCoperture(await copertureRes.json());
      }
      if (volontariRes.ok) {
        setVolontari(await volontariRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const getVolontarioName = (volontarioId) => {
    const vol = volontari.find(v => v.volontario_id === volontarioId);
    return vol ? `${vol.cognome} ${vol.nome}` : volontarioId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedVolontario) {
      toast.error('Seleziona un volontario');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/coperture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          volontario_id: selectedVolontario
        })
      });

      if (response.ok) {
        const timestamp = new Date().toLocaleString('it-IT');
        toast.success(`Copertura assicurativa registrata il ${timestamp}`);
        setDialogOpen(false);
        setFormData({
          stato: 'attiva',
          numero_polizza: '',
          compagnia: '',
          decorrenza: '',
          scadenza: '',
          tipologia: '',
          note: ''
        });
        setSelectedVolontario('');
        fetchData();
      } else {
        toast.error('Errore durante il salvataggio');
      }
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    }
  };

  // Find volunteers without coverage
  const volontariAttivi = volontari.filter(v => v.stato === 'attivo');
  const volontariConCopertura = new Set(coperture.filter(c => c.stato === 'attiva').map(c => c.volontario_id));
  const volontariSenzaCopertura = volontariAttivi.filter(v => !volontariConCopertura.has(v.volontario_id));

  // Find expiring coverages
  const oggi = new Date();
  const fra30Giorni = new Date(oggi.getTime() + 30 * 24 * 60 * 60 * 1000);
  const copertureInScadenza = coperture.filter(c => {
    if (c.stato !== 'attiva' || !c.scadenza) return false;
    const scadenza = new Date(c.scadenza);
    return scadenza >= oggi && scadenza <= fra30Giorni;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coperture-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Coperture Assicurative</h1>
          <p className="page-subtitle">Gestisci le coperture assicurative dei volontari</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-copertura-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Copertura
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nuova Copertura Assicurativa</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Volontario *</Label>
                <Select value={selectedVolontario} onValueChange={setSelectedVolontario}>
                  <SelectTrigger data-testid="select-volontario">
                    <SelectValue placeholder="Seleziona volontario" />
                  </SelectTrigger>
                  <SelectContent>
                    {volontari.map(v => (
                      <SelectItem key={v.volontario_id} value={v.volontario_id}>
                        {v.cognome} {v.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Compagnia</Label>
                  <Input
                    value={formData.compagnia}
                    onChange={(e) => setFormData(prev => ({ ...prev, compagnia: e.target.value }))}
                    placeholder="Es: Allianz"
                    data-testid="input-compagnia"
                  />
                </div>
                <div className="space-y-2">
                  <Label>N. Polizza</Label>
                  <Input
                    value={formData.numero_polizza}
                    onChange={(e) => setFormData(prev => ({ ...prev, numero_polizza: e.target.value }))}
                    placeholder="Numero polizza"
                    data-testid="input-polizza"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Decorrenza</Label>
                  <Input
                    type="date"
                    value={formData.decorrenza}
                    onChange={(e) => setFormData(prev => ({ ...prev, decorrenza: e.target.value }))}
                    data-testid="input-decorrenza"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Scadenza</Label>
                  <Input
                    type="date"
                    value={formData.scadenza}
                    onChange={(e) => setFormData(prev => ({ ...prev, scadenza: e.target.value }))}
                    data-testid="input-scadenza"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tipologia</Label>
                <Input
                  value={formData.tipologia}
                  onChange={(e) => setFormData(prev => ({ ...prev, tipologia: e.target.value }))}
                  placeholder="Es: RC terzi, Infortuni"
                  data-testid="input-tipologia"
                />
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  value={formData.note}
                  onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                  rows={2}
                  data-testid="input-note"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" className="btn-primary" data-testid="save-copertura-btn">
                  Salva Copertura
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Alerts */}
      {(volontariSenzaCopertura.length > 0 || copertureInScadenza.length > 0) && (
        <div className="grid gap-4 md:grid-cols-2">
          {volontariSenzaCopertura.length > 0 && (
            <div className="alert-warning" data-testid="alert-senza-copertura">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Volontari senza copertura</p>
                  <p className="text-sm mt-1">
                    {volontariSenzaCopertura.length} volontari attivi non hanno una copertura assicurativa valida
                  </p>
                  <ul className="text-sm mt-2 space-y-1">
                    {volontariSenzaCopertura.slice(0, 3).map(v => (
                      <li key={v.volontario_id}>• {v.cognome} {v.nome}</li>
                    ))}
                    {volontariSenzaCopertura.length > 3 && (
                      <li>... e altri {volontariSenzaCopertura.length - 3}</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}
          {copertureInScadenza.length > 0 && (
            <div className="alert-warning" data-testid="alert-in-scadenza">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Coperture in scadenza</p>
                  <p className="text-sm mt-1">
                    {copertureInScadenza.length} coperture scadranno nei prossimi 30 giorni
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-[#0055BB]" />
            Elenco Coperture
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {coperture.length === 0 ? (
            <div className="empty-state py-16">
              <Shield className="empty-state-icon" />
              <p className="empty-state-title">Nessuna copertura registrata</p>
              <p className="empty-state-text">
                Inizia aggiungendo le coperture assicurative dei volontari
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Volontario</TableHead>
                    <TableHead>Compagnia</TableHead>
                    <TableHead className="hidden md:table-cell">N. Polizza</TableHead>
                    <TableHead>Scadenza</TableHead>
                    <TableHead>Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coperture.map((copertura) => (
                    <TableRow key={copertura.copertura_id} data-testid={`copertura-row-${copertura.copertura_id}`}>
                      <TableCell className="font-medium">
                        {getVolontarioName(copertura.volontario_id)}
                      </TableCell>
                      <TableCell>{copertura.compagnia || '-'}</TableCell>
                      <TableCell className="hidden md:table-cell font-mono text-sm">
                        {copertura.numero_polizza || '-'}
                      </TableCell>
                      <TableCell>
                        {copertura.scadenza ? new Date(copertura.scadenza).toLocaleDateString('it-IT') : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[copertura.stato] || 'bg-slate-100 text-slate-800'}>
                          {copertura.stato?.charAt(0).toUpperCase() + copertura.stato?.slice(1)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <div className="disclaimer-banner">
        <p>
          <strong>Nota:</strong> La gestione delle coperture assicurative in questa sezione ha finalità di tracciabilità interna. 
          Verificare sempre con la compagnia assicurativa e il proprio consulente la validità e l'adeguatezza delle polizze.
        </p>
      </div>
    </div>
  );
}
